import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { 
  RefreshCw, TrendingUp, TrendingDown, DollarSign, 
  Activity, PieChart, BarChart3, Download 
} from 'lucide-react';
import { toast } from 'sonner';
import analyticsService from '../services/analyticsService';
import {
  LineChart, Line, BarChart, Bar, PieChart as RechartsPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart
} from 'recharts';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#6366f1', '#ec4899', '#14b8a6'];

const TradeAnalytics = ({ userId = 'demo_user' }) => {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('7d');
  const [stats, setStats] = useState({
    totalVolume: 0,
    totalTrades: 0,
    avgTradeSize: 0,
    profitLoss: 0,
    winRate: 0
  });
  const [chartData, setChartData] = useState({
    volumeOverTime: [],
    tokenDistribution: [],
    dailyTrades: [],
    priceImpact: []
  });

  const fetchTrades = async () => {
    setLoading(true);
    try {
      const limit = period === '24h' ? 50 : period === '7d' ? 200 : 500;
      const data = await analyticsService.getTradeHistory(userId, limit);
      setTrades(data);
      processTradeData(data);
    } catch (error) {
      toast.error('Failed to load trade analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrades();
  }, [userId, period]);

  const processTradeData = (tradesData) => {
    if (!tradesData || tradesData.length === 0) {
      setStats({
        totalVolume: 0,
        totalTrades: 0,
        avgTradeSize: 0,
        profitLoss: 0,
        winRate: 0
      });
      setChartData({
        volumeOverTime: [],
        tokenDistribution: [],
        dailyTrades: [],
        priceImpact: []
      });
      return;
    }

    // Calculate statistics
    const totalVolume = tradesData.reduce((sum, trade) => 
      sum + (parseFloat(trade.amount_in) || 0), 0
    );
    const totalTrades = tradesData.length;
    const avgTradeSize = totalVolume / totalTrades;

    // Calculate profit/loss (simplified - based on token price changes)
    let profitLoss = 0;
    let winningTrades = 0;
    tradesData.forEach(trade => {
      if (trade.profit) {
        profitLoss += parseFloat(trade.profit);
        if (trade.profit > 0) winningTrades++;
      }
    });
    const winRate = totalTrades > 0 ? (winningTrades / totalTrades) * 100 : 0;

    setStats({
      totalVolume: totalVolume.toFixed(2),
      totalTrades,
      avgTradeSize: avgTradeSize.toFixed(2),
      profitLoss: profitLoss.toFixed(2),
      winRate: winRate.toFixed(1)
    });

    // Process volume over time
    const volumeByDate = {};
    tradesData.forEach(trade => {
      const date = new Date(trade.timestamp).toLocaleDateString();
      if (!volumeByDate[date]) {
        volumeByDate[date] = { date, volume: 0, trades: 0 };
      }
      volumeByDate[date].volume += parseFloat(trade.amount_in) || 0;
      volumeByDate[date].trades += 1;
    });
    const volumeOverTime = Object.values(volumeByDate).sort((a, b) => 
      new Date(a.date) - new Date(b.date)
    ).map(item => ({
      ...item,
      volume: parseFloat(item.volume.toFixed(2))
    }));

    // Process token distribution
    const tokenVolumes = {};
    tradesData.forEach(trade => {
      const token = trade.token_in || 'Unknown';
      if (!tokenVolumes[token]) {
        tokenVolumes[token] = { name: token, value: 0, trades: 0 };
      }
      tokenVolumes[token].value += parseFloat(trade.amount_in) || 0;
      tokenVolumes[token].trades += 1;
    });
    const tokenDistribution = Object.values(tokenVolumes)
      .sort((a, b) => b.value - a.value)
      .slice(0, 8)
      .map(item => ({
        ...item,
        value: parseFloat(item.value.toFixed(2))
      }));

    // Process daily trades count
    const tradesByDay = {};
    tradesData.forEach(trade => {
      const date = new Date(trade.timestamp).toLocaleDateString();
      if (!tradesByDay[date]) {
        tradesByDay[date] = { date, count: 0, successful: 0, failed: 0 };
      }
      tradesByDay[date].count += 1;
      if (trade.status === 'completed') {
        tradesByDay[date].successful += 1;
      } else {
        tradesByDay[date].failed += 1;
      }
    });
    const dailyTrades = Object.values(tradesByDay).sort((a, b) => 
      new Date(a.date) - new Date(b.date)
    );

    // Process price impact data
    const priceImpact = tradesData
      .filter(trade => trade.price_impact)
      .slice(0, 20)
      .map((trade, index) => ({
        index: index + 1,
        impact: parseFloat((trade.price_impact * 100).toFixed(2)),
        volume: parseFloat(trade.amount_in) || 0
      }));

    setChartData({
      volumeOverTime,
      tokenDistribution,
      dailyTrades,
      priceImpact
    });
  };

  const exportData = () => {
    const csv = [
      ['Date', 'Type', 'Token In', 'Amount In', 'Token Out', 'Amount Out', 'Status', 'TX Hash'],
      ...trades.map(trade => [
        new Date(trade.timestamp).toLocaleString(),
        trade.type,
        trade.token_in,
        trade.amount_in,
        trade.token_out,
        trade.amount_out,
        trade.status,
        trade.tx_hash
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trade-history-${new Date().toISOString()}.csv`;
    a.click();
    toast.success('Trade history exported');
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg">
          <p className="text-sm font-medium">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Trade Analytics Dashboard
              </CardTitle>
              <CardDescription>
                Comprehensive trading insights and visualizations
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="24h">Last 24h</SelectItem>
                  <SelectItem value="7d">Last 7 days</SelectItem>
                  <SelectItem value="30d">Last 30 days</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={exportData} disabled={trades.length === 0}>
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
              <Button size="sm" variant="ghost" onClick={fetchTrades} disabled={loading}>
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Volume</p>
                <p className="text-2xl font-bold">${stats.totalVolume}</p>
              </div>
              <DollarSign className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Trades</p>
                <p className="text-2xl font-bold">{stats.totalTrades}</p>
              </div>
              <Activity className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Avg Trade Size</p>
                <p className="text-2xl font-bold">${stats.avgTradeSize}</p>
              </div>
              <BarChart3 className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Profit/Loss</p>
                <p className={`text-2xl font-bold ${parseFloat(stats.profitLoss) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  ${stats.profitLoss}
                </p>
              </div>
              {parseFloat(stats.profitLoss) >= 0 ? (
                <TrendingUp className="h-8 w-8 text-green-500" />
              ) : (
                <TrendingDown className="h-8 w-8 text-red-500" />
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Win Rate</p>
                <p className="text-2xl font-bold">{stats.winRate}%</p>
              </div>
              <PieChart className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <Tabs defaultValue="volume" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="volume">Volume Over Time</TabsTrigger>
          <TabsTrigger value="distribution">Token Distribution</TabsTrigger>
          <TabsTrigger value="daily">Daily Activity</TabsTrigger>
          <TabsTrigger value="impact">Price Impact</TabsTrigger>
        </TabsList>

        <TabsContent value="volume" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Trading Volume Over Time</CardTitle>
              <CardDescription>Total trading volume by date</CardDescription>
            </CardHeader>
            <CardContent>
              {chartData.volumeOverTime.length === 0 ? (
                <div className="h-[400px] flex items-center justify-center text-gray-500">
                  No data available for this period
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={400}>
                  <AreaChart data={chartData.volumeOverTime}>
                    <defs>
                      <linearGradient id="colorVolume" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="volume" 
                      stroke="#3b82f6" 
                      fillOpacity={1} 
                      fill="url(#colorVolume)"
                      name="Volume ($)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="distribution" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Token Distribution</CardTitle>
              <CardDescription>Trading volume by token</CardDescription>
            </CardHeader>
            <CardContent>
              {chartData.tokenDistribution.length === 0 ? (
                <div className="h-[400px] flex items-center justify-center text-gray-500">
                  No data available
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <ResponsiveContainer width="100%" height={400}>
                    <RechartsPie>
                      <Pie
                        data={chartData.tokenDistribution}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={120}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {chartData.tokenDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </RechartsPie>
                  </ResponsiveContainer>

                  <div className="space-y-3">
                    <h3 className="font-semibold mb-4">Token Breakdown</h3>
                    {chartData.tokenDistribution.map((token, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-4 h-4 rounded"
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          />
                          <div>
                            <p className="font-medium">{token.name}</p>
                            <p className="text-xs text-gray-500">{token.trades} trades</p>
                          </div>
                        </div>
                        <Badge variant="secondary">${token.value}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="daily" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Daily Trading Activity</CardTitle>
              <CardDescription>Number of trades per day</CardDescription>
            </CardHeader>
            <CardContent>
              {chartData.dailyTrades.length === 0 ? (
                <div className="h-[400px] flex items-center justify-center text-gray-500">
                  No data available
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={chartData.dailyTrades}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="successful" stackId="a" fill="#10b981" name="Successful" />
                    <Bar dataKey="failed" stackId="a" fill="#ef4444" name="Failed" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="impact" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Price Impact Analysis</CardTitle>
              <CardDescription>Price impact of recent trades</CardDescription>
            </CardHeader>
            <CardContent>
              {chartData.priceImpact.length === 0 ? (
                <div className="h-[400px] flex items-center justify-center text-gray-500">
                  No price impact data available
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={chartData.priceImpact}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="index" label={{ value: 'Trade #', position: 'insideBottom', offset: -5 }} />
                    <YAxis label={{ value: 'Price Impact (%)', angle: -90, position: 'insideLeft' }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="impact" 
                      stroke="#8b5cf6" 
                      strokeWidth={2}
                      dot={{ fill: '#8b5cf6', r: 4 }}
                      name="Impact (%)"
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TradeAnalytics;
